// BreakdownConvertDialog adım gövdeleri (spec §6.4, §18 DR6; 600 satır sınırı için ayrı dosya):
// (1) kalıp + hedef, (2) boyut + grup adı + üye adları (kalıp b: onay kutulu kardeşler), (3) dryRun
// önizleme (eski → yeni ağaç, sayılar, dokunulmayan formüller, "Geri alınamaz") ve kalıcı başarı
// paneli. Durum sihirbazda (BreakdownConvertDialog); burada yalnız görünüm.

import { useState } from "react";
import type {
  KpiBreakdownConvertResult,
  KpiBreakdownTreeNode,
  KpiBreakdownUntouchedFormula,
  KpiMetric,
  KpiRootCode,
} from "@mahir/shared";
import { KPI_BREAKDOWN_COPY } from "../../lib/kpi-breakdown-copy";
import { type ConvertForm } from "../../lib/kpi-breakdown-convert";
import {
  applyDimensionTemplate,
  type CustomDimensionOption,
  type DimensionChoice,
} from "../../lib/kpi-breakdown-dimension-ui";
import {
  setFormCustomSlug,
  setFormDimensionLabel,
  setFormLabel,
} from "../../lib/kpi-breakdown-group-form";
import { type ConvertTargetOption } from "../../lib/kpi-catalog-breakdown";
import { BreakdownDimensionPicker } from "./BreakdownDimensionPicker";
import { codeStyle, fieldStyle, inputStyle, labelStyle } from "./kpi-form-styles";

const C = KPI_BREAKDOWN_COPY.convert;
const M = KPI_BREAKDOWN_COPY.modal;
export const convertHelp = { fontSize: "0.875rem", color: "var(--color-text-3)" } as const;
const help = convertHelp;
const NO_CUSTOM: readonly CustomDimensionOption[] = [];
const NO_USED: ReadonlySet<string> = new Set();
const radioRow = { display: "flex", gap: 10, alignItems: "flex-start", padding: "8px 0" } as const;
const radioInput = { marginTop: 4, width: 18, height: 18, flex: "none" } as const;

export function ConvertStepPattern(props: {
  source: KpiMetric;
  form: ConvertForm;
  targets: readonly ConvertTargetOption[];
  locked: boolean;
  onForm: (form: ConvertForm) => void;
}): JSX.Element {
  const { form, source } = props;
  if (form.pattern === "siblings") {
    return (
      <div className="grid gap-2">
        <div>{C.patternSiblings}</div>
        <div style={help} data-testid="kpi-convert-total">
          {C.totalFixed(source.label)}
        </div>
      </div>
    );
  }
  const pick = (targetId: string) => props.onForm({ ...form, targetId });
  return (
    <div className="grid gap-2">
      <div>{C.patternCategory}</div>
      <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
        <legend style={labelStyle}>{C.targetLegend}</legend>
        <label style={radioRow}>
          <input
            type="radio"
            name="bc-target"
            value=""
            checked={form.targetId === ""}
            disabled={props.locked}
            style={radioInput}
            onChange={() => pick("")}
          />
          <span>
            {C.newTotal}
            <br />
            <span style={help}>{C.newTotalHint}</span>
          </span>
        </label>
        {props.targets.map((t) => (
          <label key={t.metric.id} style={radioRow}>
            <input
              type="radio"
              name="bc-target"
              value={t.metric.id}
              checked={form.targetId === t.metric.id}
              disabled={props.locked}
              style={radioInput}
              onChange={() => pick(t.metric.id)}
            />
            <span>
              {t.metric.label} <code style={codeStyle}>{t.metric.code}</code>
              {t.suggested ? <span style={help}> — {C.suggested}</span> : null}
              <br />
              <span style={help}>{C.siblingTargetHint}</span>
            </span>
          </label>
        ))}
      </fieldset>
    </div>
  );
}

export function ConvertStepDimension(props: {
  form: ConvertForm;
  locked: boolean;
  rootCode?: KpiRootCode | null | undefined;
  custom?: readonly CustomDimensionOption[] | undefined;
  onForm: (form: ConvertForm) => void;
}): JSX.Element {
  const { form, onForm } = props;
  const g = form.group;
  const custom = props.custom ?? NO_CUSTOM;
  const setGroup = (group: ConvertForm["group"]) => onForm({ ...form, group });
  const choose = (choice: DimensionChoice): void => {
    const slug = choice.kind === "preset" ? choice.slug : choice.dim.slug;
    if (slug === g.dimension) return;
    // üyeler bu sihirbazda kardeşlerden gelir — boyut şablonunun örnek üyeleri kullanılmaz
    setGroup({ ...applyDimensionTemplate(g, choice, []), members: [] });
  };
  const [labelOpen, setLabelOpen] = useState(false);
  const showLabel = g.dimension !== "diger" || labelOpen || g.labelTouched;
  const setMember = (metricId: string, patch: Partial<ConvertForm["members"][number]>) =>
    onForm({
      ...form,
      members: form.members.map((m) => (m.metricId === metricId ? { ...m, ...patch } : m)),
    });
  const siblings = form.pattern === "siblings";
  return (
    <div className="grid gap-3">
      <BreakdownDimensionPicker
        form={g}
        rootCode={props.rootCode ?? null}
        custom={custom}
        used={NO_USED}
        disabled={props.locked}
        onChoose={choose}
        onDimensionLabel={(v) => setGroup(setFormDimensionLabel(g, v))}
        onSlug={(v) => setGroup(setFormCustomSlug(g, v))}
      />
      {showLabel ? (
        <div style={fieldStyle}>
          <label style={labelStyle} htmlFor="bc-label">
            {M.groupLabel}
          </label>
          <input
            id="bc-label"
            style={inputStyle}
            value={g.label}
            maxLength={200}
            disabled={props.locked}
            onChange={(e) => setGroup(setFormLabel(g, e.target.value))}
          />
        </div>
      ) : (
        <button
          type="button"
          data-testid="kpi-convert-show-label"
          disabled={props.locked}
          onClick={() => setLabelOpen(true)}
          style={{
            ...help,
            justifySelf: "start",
            background: "transparent",
            border: 0,
            padding: 0,
            minHeight: 32,
            color: "var(--color-primary)",
            textDecoration: "underline",
            cursor: "pointer",
          }}
        >
          {M.showGroupLabel}
        </button>
      )}
      <fieldset style={{ border: 0, padding: 0, margin: 0 }} data-testid="kpi-convert-members">
        <legend style={labelStyle}>{siblings ? C.siblingsLegend : C.membersLegend}</legend>
        {siblings ? <div style={help}>{C.siblingsHint}</div> : null}
        <div
          aria-hidden="true"
          className="flex gap-2"
          style={{ ...help, padding: "6px 0 0", textAlign: "right" }}
        >
          <span style={{ flex: "1 1 160px" }} />
          <span style={{ flex: "1 1 160px", textAlign: "left" }}>{C.memberNameHeader}</span>
        </div>
        {form.members.map((m) => (
          <div
            key={m.metricId}
            className="flex items-center gap-2 flex-wrap"
            style={{ padding: "4px 0", minHeight: 44 }}
          >
            {siblings ? (
              <input
                type="checkbox"
                aria-label={m.label}
                checked={m.checked}
                disabled={props.locked || m.ineligible !== undefined}
                style={{ width: 18, height: 18 }}
                onChange={(e) => setMember(m.metricId, { checked: e.target.checked })}
              />
            ) : null}
            <span style={{ flex: "1 1 160px", minWidth: 0 }}>
              {m.label}
              {m.ineligible ? (
                <span style={help} data-testid="kpi-convert-ineligible">
                  {" — "}
                  {m.ineligible === "unit"
                    ? C.ineligible.unit(m.unit ?? null)
                    : C.ineligible[m.ineligible]}
                </span>
              ) : null}
            </span>
            <span aria-hidden="true" style={help}>
              {C.arrow}
            </span>
            <input
              aria-label={C.memberLabelAria(m.label)}
              style={{ ...inputStyle, flex: "1 1 160px", width: "auto" }}
              value={m.memberLabel}
              maxLength={200}
              disabled={props.locked || !m.checked}
              onChange={(e) => setMember(m.metricId, { memberLabel: e.target.value })}
            />
          </div>
        ))}
      </fieldset>
    </div>
  );
}

function TreeList(props: { node: KpiBreakdownTreeNode; depth?: number }): JSX.Element {
  const depth = props.depth ?? 0;
  const n = props.node;
  return (
    <li style={{ paddingLeft: depth === 0 ? 0 : 14 }}>
      {n.label ? (
        <span>
          {n.label} <code style={codeStyle}>{n.code}</code>
        </span>
      ) : null}
      {n.children.length > 0 ? (
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {n.children.map((c) => (
            <TreeList key={c.code} node={c} depth={depth + 1} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

export function UntouchedFormulas(props: {
  list: readonly KpiBreakdownUntouchedFormula[];
}): JSX.Element {
  if (props.list.length === 0) return <div style={help}>{C.untouchedNone}</div>;
  return (
    <div data-testid="kpi-convert-untouched">
      <div style={help}>{C.untouchedLead}</div>
      <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
        {props.list.map((f, i) => (
          <li key={`${f.setId ?? "c"}:${f.code}:${i.toString()}`} style={{ fontSize: "0.875rem" }}>
            <code style={codeStyle}>{f.code}</code> = <code style={codeStyle}>{f.expression}</code>{" "}
            <span style={help}>({C.untouchedScope(f.setId)})</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ConvertStepPreview(props: {
  preview: KpiBreakdownConvertResult | null;
  scanning: boolean;
  scanSets: number | null;
}): JSX.Element | null {
  if (props.scanning) {
    return (
      <div role="status" aria-busy="true" className="grid gap-2" data-testid="kpi-convert-scanning">
        <span style={help}>{C.scanning(props.scanSets)}</span>
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            aria-hidden="true"
            style={{
              height: 14,
              borderRadius: 6,
              background: "var(--color-bg)",
              width: `${(90 - i * 20).toString()}%`,
            }}
          />
        ))}
      </div>
    );
  }
  const p = props.preview;
  if (!p) return null;
  if (p.noop) return <div role="status">{C.noop}</div>;
  return (
    <div className="grid gap-3" data-testid="kpi-convert-preview">
      <div
        className="grid gap-3"
        style={{
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          fontSize: "0.875rem",
        }}
      >
        {(["before", "after"] as const).map((k) => (
          <div
            key={k}
            className="rounded-lg p-2"
            style={{ border: "1px solid var(--color-border)" }}
          >
            <div style={labelStyle}>{C[k]}</div>
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              <TreeList node={p.tree[k]} />
            </ul>
          </div>
        ))}
      </div>
      <strong data-testid="kpi-convert-counts">{C.counts(p.sets, p.formulas, p.values)}</strong>
      <UntouchedFormulas list={p.untouchedFormulas} />
      <div style={{ color: "var(--color-warning-text)", fontWeight: 600 }}>{C.irreversible}</div>
    </div>
  );
}

export function ConvertSuccessPanel(props: {
  result: KpiBreakdownConvertResult;
  groupLabel: string;
}): JSX.Element {
  const r = props.result;
  return (
    <div
      role="status"
      className="grid gap-2 rounded-lg px-3 py-2"
      data-testid="kpi-convert-success"
      style={{
        background: "color-mix(in srgb, var(--color-primary) 6%, white)",
        border: "1px solid var(--color-border)",
      }}
    >
      <strong>{C.success(props.groupLabel)}</strong>
      <span>{C.successCounts(r.sets, r.formulas, r.values)}</span>
      <UntouchedFormulas list={r.untouchedFormulas} />
    </div>
  );
}
